import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  TextField, 
  Button, 
  Card, 
  CardContent, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemSecondaryAction,
  IconButton,
  Divider,
  Chip,
  Avatar,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as CartIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  Clear as ClearIcon,
  Discount as DiscountIcon,
  LocalOffer as OfferIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import dutchBanglaPOSService from '../../services/dutchBanglaPOSService';
import multiBankPOSService from '../../services/multiBankPOSService';

const SalesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const barcodeTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);
  const [products, setProducts] = useState([
    {
      id: 1,
      name: 'মসুর ডাল',
      price: 120,
      stock: 50,
      category: 'ডাল',
      barcode: '123456789'
    },
    {
      id: 2,
      name: 'চাল',
      price: 65,
      stock: 100,
      category: 'চাল',
      barcode: '987654321'
    },
    {
      id: 3,
      name: 'চিনি',
      price: 95,
      stock: 30,
      category: 'মিষ্টি',
      barcode: '456789123'
    },
    {
      id: 4,
      name: 'আটা',
      price: 55,
      stock: 75,
      category: 'আটা',
      barcode: '789123456'
    },
    {
      id: 5,
      name: 'তেল',
      price: 180,
      stock: 25,
      category: 'তেল',
      barcode: '321654987'
    }
  ]);
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [selectedBank, setSelectedBank] = useState('dbbl');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const filtered = products.filter(product =>
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.barcode.includes(searchTerm)
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  // Barcode Scanner Integration
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Most barcode scanners end with Enter key
      if (event.key === 'Enter' && barcodeBuffer.length > 0) {
        handleBarcodeScanned(barcodeBuffer);
        setBarcodeBuffer('');
        setIsScanning(false);
        return;
      }
      
      // Ignore special keys, only collect alphanumeric characters
      if (event.key.length === 1 && !event.ctrlKey && !event.altKey && !event.metaKey) {
        setBarcodeBuffer(prev => prev + event.key);
        setIsScanning(true);
        
        // Clear buffer after 100ms if no more input (prevents false scans)
        if (barcodeTimeoutRef.current) {
          clearTimeout(barcodeTimeoutRef.current);
        }
        barcodeTimeoutRef.current = setTimeout(() => {
          setBarcodeBuffer('');
          setIsScanning(false);
        }, 100);
      }
    };

    // Only listen for barcode input when search field is not focused
    if (!searchInputRef.current?.contains(document.activeElement)) {
      document.addEventListener('keypress', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keypress', handleKeyPress);
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
    };
  }, [barcodeBuffer]);

  const handleBarcodeScanned = (barcode) => {
    console.log('Barcode scanned:', barcode);
    
    // Find product by barcode
    const product = products.find(p => p.barcode === barcode);
    
    if (product) {
      addToCart(product);
      setSnackbar({ 
        open: true, 
        message: `${product.name} স্ক্যান করা হয়েছে এবং কার্টে যোগ করা হয়েছে`, 
        severity: 'success' 
      });
    } else {
      setSnackbar({ 
        open: true, 
        message: `বারকোড ${barcode} এর জন্য কোন পণ্য পাওয়া যায়নি`, 
        severity: 'error' 
      });
    }
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item =>
        item.id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setSnackbar({ open: true, message: `${product.name} কার্টে যোগ করা হয়েছে`, severity: 'success' });
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const getTotalAmount = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getDiscountAmount = () => {
    return (getTotalAmount() * discount) / 100;
  };

  const getFinalAmount = () => {
    return getTotalAmount() - getDiscountAmount();
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      setSnackbar({ open: true, message: 'কার্টে কোন পণ্য নেই', severity: 'error' });
      return;
    }
    setShowPaymentDialog(true);
  };

  const processPayment = async () => {
    try {
      // Prepare sale data
      const saleData = {
        items: cart,
        total: getTotalAmount(),
        discount: getDiscountAmount(),
        finalAmount: getFinalAmount(),
        paymentMethod,
        selectedBank,
        timestamp: new Date().toISOString()
      };
      
      console.log('Processing sale:', saleData);

      // Process payment based on method
      let paymentResult;
      
      if (paymentMethod === 'card') {
        // Use Multi-Bank POS for card payments
        paymentResult = await multiBankPOSService.processCardPayment(selectedBank, {
          amount: getFinalAmount(),
          cardType: 'mastercard', // This would be selected by user
          cardNumber: '4111111111111111', // This would come from card reader
          expiryDate: '12/25', // This would come from card reader
          cvv: '123', // This would come from card reader
          merchantId: 'YOUR_MERCHANT_ID', // Configure this
          terminalId: 'YOUR_TERMINAL_ID' // Configure this
        });
      } else if (paymentMethod === 'mobile') {
        // Mobile banking integration (separate service)
        paymentResult = { success: true, message: 'Mobile banking payment processed' };
      } else {
        // Cash payment
        paymentResult = { success: true, message: 'Cash payment received' };
      }

      if (paymentResult.success) {
        // Clear cart and close dialog
        setCart([]);
        setDiscount(0);
        setShowPaymentDialog(false);
        
        // Show success message with transaction details
        const successMessage = paymentMethod === 'card' 
          ? `${paymentResult.bankName} - বিক্রয় সফল! ট্রানজেকশন আইডি: ${paymentResult.transactionId}`
          : 'বিক্রয় সফলভাবে সম্পন্ন হয়েছে!';
          
        setSnackbar({ 
          open: true, 
          message: successMessage, 
          severity: 'success' 
        });

        // Here you would typically send the complete sale data to your backend
        // await saveSaleToDatabase(saleData, paymentResult);
        
      } else {
        // Show payment error
        const errorMessage = paymentMethod === 'card'
          ? `${paymentResult.bankName} - পেমেন্ট ব্যর্থ: ${paymentResult.error}`
          : `পেমেন্ট ব্যর্থ: ${paymentResult.error}`;
          
        setSnackbar({ 
          open: true, 
          message: errorMessage, 
          severity: 'error' 
        });
      }
      
    } catch (error) {
      console.error('Payment processing error:', error);
      setSnackbar({ 
        open: true, 
        message: 'পেমেন্ট প্রক্রিয়াকরণে ত্রুটি হয়েছে', 
        severity: 'error' 
      });
    }
  };

  const clearCart = () => {
    setCart([]);
    setDiscount(0);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          নতুন বিক্রয় (New Sale)
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearCart}
            disabled={cart.length === 0}
          >
            কার্ট খালি করুন
          </Button>
          <Button
            variant="contained"
            startIcon={<ReceiptIcon />}
            onClick={handleCheckout}
            disabled={cart.length === 0}
            sx={{ 
              background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #3d1789 30%, #0a6abd 90%)'
              }
            }}
          >
            বিক্রয় সম্পন্ন করুন
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Product Search and List */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              পণ্য অনুসন্ধান
            </Typography>
            
            <TextField
              fullWidth
              placeholder="পণ্যের নাম বা বারকোড লিখুন..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: isScanning && (
                  <InputAdornment position="end">
                    <Chip 
                      label="স্ক্যান হচ্ছে..." 
                      size="small" 
                      color="primary" 
                      icon={<SearchIcon />}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
              ref={searchInputRef}
            />

            {/* Barcode Scanner Status */}
            {isScanning && (
              <Alert severity="info" sx={{ mb: 2 }}>
                বারকোড স্ক্যানার সক্রিয় - পণ্য স্ক্যান করুন
              </Alert>
            )}

            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Grid container spacing={2}>
                {filteredProducts.map((product) => (
                  <Grid item xs={12} sm={6} key={product.id}>
                    <Card 
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { 
                          boxShadow: 3,
                          transform: 'translateY(-2px)',
                          transition: 'all 0.2s'
                        }
                      }}
                      onClick={() => addToCart(product)}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 1, width: 32, height: 32 }}>
                            {product.name.charAt(0)}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                            {product.name}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {product.category}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                            ৳{product.price}
                          </Typography>
                          <Chip 
                            label={`স্টক: ${product.stock}`} 
                            size="small" 
                            color={product.stock < 10 ? 'error' : 'default'}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Shopping Cart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '70vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CartIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                বিক্রয় কার্ট ({cart.length} আইটেম)
              </Typography>
            </Box>

            {cart.length === 0 ? (
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'text.secondary'
              }}>
                <CartIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6">কার্ট খালি</Typography>
                <Typography variant="body2">পণ্য যোগ করতে বাম দিকে ক্লিক করুন</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ flex: 1, overflow: 'auto', mb: 2 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>পণ্য</TableCell>
                          <TableCell align="right">দাম</TableCell>
                          <TableCell align="center">পরিমাণ</TableCell>
                          <TableCell align="right">মোট</TableCell>
                          <TableCell align="center">কার্যক্রম</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cart.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.category}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">৳{item.price}</TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <IconButton 
                                  size="small" 
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                >
                                  <RemoveIcon />
                                </IconButton>
                                <Typography sx={{ mx: 1, minWidth: 20, textAlign: 'center' }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="right">৳{item.price * item.quantity}</TableCell>
                            <TableCell align="center">
                              <IconButton 
                                size="small" 
                                color="error"
                                onClick={() => removeFromCart(item.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Cart Summary */}
                <Box sx={{ space: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography>মোট মূল্য:</Typography>
                    <Typography>৳{getTotalAmount().toFixed(2)}</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TextField
                      size="small"
                      label="ছাড় (%)"
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
                      InputProps={{
                        startAdornment: <DiscountIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                      }}
                      sx={{ width: 120, mr: 2 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      ছাড়: ৳{getDiscountAmount().toFixed(2)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      চূড়ান্ত মূল্য:
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      ৳{getFinalAmount().toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onClose={() => setShowPaymentDialog(false)} maxWidth="sm" fullWidth disableScrollLock={true}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">পেমেন্ট সম্পন্ন করুন</Typography>
            <IconButton onClick={() => setShowPaymentDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box spacing={3}>
            {/* Payment Summary */}
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>বিলের বিবরণ</Typography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>মোট মূল্য:</Typography>
                <Typography>৳{getTotalAmount().toFixed(2)}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography>ছাড়:</Typography>
                <Typography>-৳{getDiscountAmount().toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box display="flex" justifyContent="space-between">
                <Typography variant="h6">সর্বমোট:</Typography>
                <Typography variant="h6" color="primary">৳{getFinalAmount().toFixed(2)}</Typography>
              </Box>
            </Paper>

            {/* Payment Method Selection */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
              <FormLabel component="legend">পেমেন্ট পদ্ধতি</FormLabel>
              <RadioGroup value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <FormControlLabel value="cash" control={<Radio />} label="নগদ" />
                <FormControlLabel value="card" control={<Radio />} label="কার্ড" />
                <FormControlLabel value="mobile" control={<Radio />} label="মোবাইল ব্যাংকিং" />
              </RadioGroup>
            </FormControl>

            {/* Bank Selection for Card Payments */}
            {paymentMethod === 'card' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>ব্যাংক নির্বাচন করুন</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  label="ব্যাংক নির্বাচন করুন"
                >
                  {multiBankPOSService.getSupportedBanks().map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      <Box display="flex" alignItems="center">
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: bank.color,
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {bank.shortName}
                        </Box>
                        {bank.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Payment Instructions */}
            {paymentMethod === 'card' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {multiBankPOSService.getBank(selectedBank)?.name} POS মেশিন ব্যবহার করে পেমেন্ট গ্রহণ করা হবে।
                  কার্ডটি স্ক্যান করুন এবং PIN নম্বর দিন।
                </Typography>
              </Alert>
            )}

            {paymentMethod === 'mobile' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  মোবাইল ব্যাংকিং এর মাধ্যমে পেমেন্ট করুন। বিকাশ, নগদ, রকেট ইত্যাদি।
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaymentDialog(false)}>বাতিল</Button>
          <Button 
            onClick={processPayment} 
            variant="contained" 
            color="primary"
            startIcon={<PaymentIcon />}
          >
            পেমেন্ট সম্পন্ন করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalesPage; 